// ============================================================
// SaveIndicator.tsx
// Auto-save status pill in the viewing-pane header.
// States: idle | saving | saved | error
//
// Two state sources:
//  - currentCase.updatedAt drives the saving → saved → idle cascade
//    (proxy for "the user just edited something")
//  - saveError, set by useAutoSave when a save fails, takes precedence
//    over everything else and renders the red "Couldn't save" visual.
//    It clears on the next successful save.
//
// The wrapper is always rendered with a fixed min-width so the
// surrounding flex row in ViewingPaneHeader doesn't reflow when the
// indicator goes from showing to hiding. We fade the contents via
// opacity instead of using display:none.
// ============================================================
import { flushDeadlineAutoSave } from '../../hooks/useCaseDeadlineAutoSave'
import { flushWitnessAutoSave } from '../../hooks/useWitnessAutoSave'
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { retryAutoSave } from '../../hooks/useAutoSave'
import { heldContactMessage, useHeldContact } from '../../hooks/heldContact'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

// Inline spinner — mirrors the LoginScreen pattern; `currentColor` lets
// it inherit the surrounding text colour.
const Spinner: React.FC<{ className?: string }> = ({ className = 'h-3 w-3' }) => (
  <svg
    className={`animate-spin ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
    />
  </svg>
)

// Small tick used for the 'saved' state.
const Tick: React.FC<{ className?: string }> = ({ className = 'h-3 w-3' }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 8.5l3 3 7-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Roughly the auto-save debounce (1000ms in useAutoSave) plus a typical
// request RTT — enough wall-time that 'Saving…' is actually visible.
const SAVING_VISIBLE_MS = 1200
// How long 'Saved' lingers before we fade back to idle.
const SAVED_LINGER_MS = 3000

const SaveIndicator: React.FC = () => {
  const currentCase = useCasevoStore((state) => state.currentCase)
  const saveError = useCasevoStore((state) => state.saveError)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const lastSeenUpdatedAtRef = useRef<string | null>(null)
  const savingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Manual-retry state. 'retrying' = button just clicked, request in
  // flight, pill should show 'Saving...'. 'just-succeeded' = retry
  // returned success, pill briefly shows 'Saved' before fading back
  // to whatever the natural state is. Mirrors the cascade's tone so
  // a manual save looks the same as an auto save.
  const [retryState, setRetryState] = useState<'idle' | 'retrying' | 'just-succeeded'>('idle')
  // Ref-based double-click guard. Two clicks within the same render
  // cycle would both read retryState === 'idle' from their closures,
  // so we need a mutable cell that's always current.
  const retryingRef = useRef<boolean>(false)
  const justSucceededTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Watch updatedAt and run the saving → saved → idle cascade.
  useEffect(() => {
    if (!currentCase) return
    if (currentCase.updatedAt === lastSeenUpdatedAtRef.current) return
    lastSeenUpdatedAtRef.current = currentCase.updatedAt

    // Clear any in-flight timers from a previous edit so consecutive
    // changes don't stack up and finish in the wrong order.
    if (savingTimerRef.current) clearTimeout(savingTimerRef.current)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)

    setStatus('saving')
    savingTimerRef.current = setTimeout(() => {
      setStatus('saved')
      savedTimerRef.current = setTimeout(() => {
        setStatus('idle')
      }, SAVED_LINGER_MS)
    }, SAVING_VISIBLE_MS)

    return () => {
      if (savingTimerRef.current) clearTimeout(savingTimerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [currentCase])

  // Manual retry takes precedence over everything — when the button is
  // clicked we want the pill to show 'Saving...' immediately and a brief
  // 'Saved' on success, regardless of whatever the cascade left behind.
  // Then we fall back to the existing precedence (error → cascade status).
  const displayStatus: SaveStatus =
    retryState === 'retrying' ? 'saving'
    : retryState === 'just-succeeded' ? 'saved'
    : saveError !== null ? 'error'
    : status

  const handleRetry = async () => {
    if (retryingRef.current) return
    retryingRef.current = true
    setRetryState('retrying')
    try {
      // Retry every autosave stream — case, witness and deadline rows share
      // this indicator (previously only the case stream was retried, so
      // "Try again" did nothing for a witness/deadline failure).
      await Promise.allSettled([retryAutoSave(), flushWitnessAutoSave(), flushDeadlineAutoSave()])
      // After the retry settles, check whether saveError cleared
      // (success) or stayed (failure). On success, briefly show 'Saved';
      // on failure, drop straight back to the error pill + button.
      const errorAfter = useCasevoStore.getState().saveError
      if (errorAfter === null) {
        setRetryState('just-succeeded')
        if (justSucceededTimerRef.current) clearTimeout(justSucceededTimerRef.current)
        justSucceededTimerRef.current = setTimeout(() => {
          setRetryState('idle')
        }, SAVED_LINGER_MS)
      } else {
        setRetryState('idle')
      }
    } finally {
      retryingRef.current = false
    }
  }

  // Tidy up the 'just-succeeded' timer on unmount.
  useEffect(() => {
    return () => {
      if (justSucceededTimerRef.current) clearTimeout(justSucceededTimerRef.current)
    }
  }, [])

  // (Batch 2, M17) A half-typed email / phone held back by the autosave is
  // NOT saved — the pill says so instead of "Saved".
  const held = heldContactMessage(useHeldContact())
  // Per-state styling helpers
  const isVisible = displayStatus !== 'idle' || !!held
  const colourClass =
    displayStatus === 'error' ? 'text-red-400' : 'text-casevo-light'

  // When there's no status to show, render NOTHING — not a zero-width span.
  // As a flex child in the AppHeader right cluster (bell · [this] · avatar),
  // even a 0-width element still consumes one of the cluster's flex GAP slots,
  // which pushed the avatar right on the screens that mount SaveIndicator
  // (ClientSearch / client detail) versus those that don't (dashboard, case
  // list). Returning null removes the gap slot so the avatar sits flush,
  // matching every other screen. On the case page the indicator still renders
  // whenever a save is actually happening (saving / saved / error).
  if (!isVisible) return null

  return (
    <span
      // Live region for screen readers — announces save state changes.
      aria-live="polite"
      // min-w-[5rem] is reserved ONLY while a status is visible (keeps the
      // width steady across Saving…/Saved/error so the avatar doesn't jiggle
      // between states). When idle/invisible we DON'T reserve it — otherwise an
      // always-idle SaveIndicator (e.g. the ClientSearch header, which only
      // ever surfaces errors) leaves an 80px void between the bell and the
      // avatar, mis-placing the bell on desktop and cramping the row on mobile.
      className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap
                  transition-opacity duration-300
                  ${colourClass}
                  ${isVisible ? 'min-w-[5rem] opacity-100' : 'opacity-0'}`}
    >
      {displayStatus === 'saving' && (
        <>
          <Spinner />
          <span>Saving...</span>
        </>
      )}
      {held && displayStatus !== 'saving' && displayStatus !== 'error' && <span>{held}</span>}
      {displayStatus === 'saved' && !held && (
        <>
          <Tick className="h-3 w-3 text-casevo-mint" />
          <span>Saved</span>
        </>
      )}
      {displayStatus === 'error' && (
        <>
          <span>Couldn't save —</span>
          <button
            type="button"
            onClick={handleRetry}
            className="underline hover:text-red-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-300 rounded-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            try again
          </button>
        </>
      )}
    </span>
  )
}

export default SaveIndicator
